import {
	useState,
	useRef,
	useContext,
	useEffect,
	useLayoutEffect
} from "react";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import MuiListItem from "@mui/material/ListItem";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import {
	ComponentPropsContext,
	ComponentPropsContextApi
} from "layouts/Flow Designer/context";
import "./style.css";
import TagFileListItem from "./component/TagFileListItem";
import { getEnv } from "helpers/environment";

const ListItem = styled(MuiListItem)(() => ({
	width: "100%",
	marginTop: "16px"
}));

const FileListItem = ({ action, fieldValue, allowEditWorkflow }) => {
	const [fileNames, setFileNames] = useState([]);
	const [fileData, setFileData] = useState([]);
	const [tagFileNames, setTagFileNames] = useState([]);
	const [showTagFileNames, setShowTagFileNames] = useState(false);
	const textInputRef = useRef(null);
	const scrollRef = useRef(null);
	const fileInputRef = useRef(null);
	const { actionForm, formErrors } = useContext(ComponentPropsContext);
	const { setActionForm, setFormErrors } = useContext(
		ComponentPropsContextApi
	);
	const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
	const isFlowActive = currentSavedFlow?.shared_status === "active";

	const handleFileChange = (e) => {
		const displayFile = e.target.files;
		if (displayFile.length > 0) {
			setFileNames((prevFileNames) => {
				const newFileNamesArray = Array.from(displayFile)
					.map((file) => file.name)
					.filter((fileName) => !prevFileNames.includes(fileName));

				return [...prevFileNames, ...newFileNamesArray];
			});

			const { name } = e.target;
			const files = e.target.files;

			if (files) {
				const promises = Object.keys(files).map((key) => {
					return new Promise((resolve) => {
						const reader = new FileReader();
						reader.onload = () => {
							const base64Content = reader.result;
							resolve({
								filename: files[key].name,
								size: files[key].size,
								path: base64Content,
								type: files[key].type
							});
						};
						reader.readAsDataURL(files[key]);
					});
				});

				Promise.all(promises).then((fileObjects) => {
					setTagFileNames((prevTagFileNames) => {
						const newTagFileNamesArray = fileObjects.map((file) => ({
							value: file.filename,
							isVariable: false
						}));

						const existingFileNames = prevTagFileNames.map(
							(tag) => tag.value
						);
						const uniqueNewTags = newTagFileNamesArray.filter(
							(tag) => !existingFileNames.includes(tag.value)
						);

						return [...prevTagFileNames, ...uniqueNewTags];
					});

					// Update file data
					setFileData((prevFileData) => {
						const newFileDataArray = fileObjects.filter(
							(file) =>
								!prevFileData.some(
									(prevFile) => prevFile.filename === file.filename
								)
						);

						return [...prevFileData, ...newFileDataArray];
					});

					// Update action form
					setActionForm((prevActionForm) => {
						const existingFileData = prevActionForm[name]?.value || [];
						const uniqueNewFileData = fileObjects.filter(
							(file) =>
								!existingFileData.some(
									(prevFile) => prevFile.filename === file.filename
								)
						);

						return {
							...prevActionForm,
							[name]: {
								value: [...existingFileData, ...uniqueNewFileData]
							}
						};
					});
				});
			}

			if (textInputRef.current) {
				textInputRef.current.focus();
				setShowTagFileNames(true);
			}
		}
	};

	useEffect(() => {
		if (fieldValue?.length) {
			const fileNamesArray = fieldValue.map((file) => file.filename);

			const newTagFileNames = [
				...tagFileNames,
				...fieldValue.map((file) => ({
					value: file.filename,
					isVariable: false
				}))
			];

			setFileData(fieldValue);
			setTagFileNames(newTagFileNames);
			setFileNames((prev) => [...prev, ...fileNamesArray]);
		}
	}, []);

	const handleShowFileNameList = () => {
		setShowTagFileNames(true);
		if (scrollRef.current) {
			scrollRef.current.scrollIntoView({
				behavior: "smooth",
				block: "end",
				inline: "nearest"
			});
		}
	};

	const handleRemoveFile = (files) => {
		const newTagFileNames = [
			...files.map((file) => ({
				value: file,
				isVariable: false
			}))
		];
		const filteredArray = fileData.filter((item) =>
			files.includes(item.filename)
		);

		setActionForm({
			...actionForm,
			[action.field]: { value: filteredArray }
		});
		setFileNames(files);
		setTagFileNames(newTagFileNames);
	};

	useLayoutEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollIntoView({
				behavior: "smooth",
				block: "end",
				inline: "nearest"
			});
		}
	}, [tagFileNames.length, showTagFileNames]);

	const handleChooseFile = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleClickInputFile = () => {
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	useEffect(() => {
		if (!fieldValue?.length) return;

		const totalFilesSize = fieldValue.reduce(
			(accu, curr) => accu + curr.size,
			0
		);

		if (totalFilesSize > getEnv("MAX_FILE_COMBINED_SIZE")) {
			setFormErrors((prev) => prev.concat(action.field));
		} else {
			setFormErrors((prev) =>
				prev.filter((error) => error !== action.field)
			);
		}
	}, [fieldValue?.length]);

	return (
		<ListItem key={action.label}>
			<SoftBox
				display="flex"
				flexDirection="column"
				width="100%"
			>
				<SoftTypography
					variant="subtitle2"
					fontWeight="light"
					color="secondary"
					sx={{
						fontSize: "12px",
						marginBottom: "5px",
						fontWeight: "normal"
					}}
				>
					{action.label}
				</SoftTypography>
				<SoftBox
					className="filelist-container"
					sx={{
						position: "relative",
						height: "38.8px",
						borderRadius: ".5rem",
						display:
							showTagFileNames && tagFileNames.length > 0
								? "none"
								: "flex"
					}}
				>
					<SoftInput
						type="file"
						onChange={handleFileChange}
						inputProps={{
							multiple: action.multiple ? true : false,
							ref: fileInputRef
						}}
						sx={{
							borderTopRightRadius: 0,
							borderBottomRightRadius: 0,
							paddingRight: 0,
							width: "111px !important",
							height: "100% !important",
							"& .MuiInputBase-input": {
								color: "transparent !important"
							},
							borderBottomWidth: "1px !important"
						}}
						style={{
							boxShadow: "none",
							position: "absolute",
							top: "0",
							left: "0",
							zIndex: 1,
							borderRight: 0
						}}
						name={action.field}
						// value={fileInputValue}
						onClick={handleClickInputFile}
						disabled={isFlowActive || !allowEditWorkflow}
					/>
					<SoftInput
						inputRef={textInputRef}
						type="text"
						readOnly
						disabled={
							fileNames.length === 0 ||
							isFlowActive ||
							!allowEditWorkflow
						}
						value={fileNames.join(", ")}
						sx={{
							borderTopLeftRadius: 0,
							borderBottomLeftRadius: 0,
							borderLeft: 0,
							paddingLeft: "5px",
							marginLeft: "110px",
							backgroundColor: "transparent !important",
							"&.Mui-focused": {
								content: '""',
								top: 0,
								left: 0,
								zIndex: 9,
								boxShadow: "none !important"
							},
							borderBottomWidth: "1px !important"
						}}
						placeholder="No file chosen"
						onClick={handleShowFileNameList}
					/>
				</SoftBox>
				<div
					ref={scrollRef}
					style={{
						display:
							showTagFileNames && tagFileNames.length > 0
								? "block"
								: "none"
					}}
				>
					<TagFileListItem
						tagFileNames={tagFileNames}
						fieldValue={fieldValue}
						action={action}
						handleRemoveFile={handleRemoveFile}
						handleChooseFile={handleChooseFile}
						allowEditWorkflow={allowEditWorkflow}
					/>
					{formErrors.includes(action.field) && (
						<SoftTypography
							sx={{
								color: "#ff667c",
								fontSize: "12px"
							}}
						>
							Total file size should not exceed 10mb
						</SoftTypography>
					)}
				</div>
			</SoftBox>
		</ListItem>
	);
};

export default FileListItem;
